import React, { useEffect, createElement } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ErrorBoundary from './components/ui/ErrorBoundary';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Services from './pages/Services';
import MentalCapacity from './pages/Services/MentalCapacity';
import TraumaPTSD from './pages/Services/TraumaPTSD';
import Specialist from './pages/Services/Specialist';
import Privacy from './pages/Policies/Privacy';
import Terms from './pages/Policies/Terms';
import Cancellation from './pages/Policies/Cancellation';
import ForensicRisk from './pages/Services/ForensicRisk';
import Neurodevelopmental from './pages/Services/Neurodevelopmental';
import ClinicalNegligence from './pages/Services/ClinicalNegligence';
import Sitemap from './pages/Sitemap';
import NotFound from './pages/NotFound';
import CookieConsent from './components/ui/CookieConsent';
// Google Analytics 4, Microsoft Clarity and the LinkedIn Insight Tag are loaded
// only after consent, from utils/consent.ts via <CookieConsent />.
// ScrollToTop component to ensure page loads at the top
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    // Honour in-page anchors such as /policies/privacy#cookies
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}
// Skip to content link for accessibility
function SkipToContent() {
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-secondary focus:text-white focus:rounded-md focus:shadow-lg" data-id="element-1" data-mp="3beu8ls88hnkn">
      Skip to main content
    </a>;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-2" data-mp="3ihsbsifssmen">
      {/* Site-wide head only. Per-page title, description, canonical, Open Graph,
          Twitter card and page schema are set by components/ui/SEO.tsx. */}
      <Helmet data-id="element-3" data-mp="3nfais3a98rk0">
        <html lang="en-GB" data-id="element-4" data-mp="3sfyakr6b5cfa" />
        <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-5" data-mp="3sf6rugxt796f" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" data-id="element-6" data-mp="2uspwhh8mz5nb" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet" data-id="element-7" data-mp="2zqrgc4dvz8zu" />
        <link rel="icon" href="https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png" type="image/png" data-id="element-8" data-mp="39h3zbs61yu3v" />
        <link rel="preconnect" href="https://uploadthingy.s3.us-west-1.amazonaws.com" crossOrigin="" data-id="element-9" data-mp="2c05eiuuh6kya" />
        {/* Above-the-fold logo, preloaded to protect largest contentful paint */}
        <link rel="preload" as="image" href="https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png" data-id="element-10" data-mp="u3tqet94vas4" />
        <script type="application/ld+json" data-id="element-11" data-mp="k65tpxryvt7b">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Medicolegal Psychiatry",
              "url": "https://medicolegal-psychiatry.co.uk",
              "email": "enquiries@medicolegal-psychiatry.co.uk",
              "areaServed": ["England", "Wales"],
              "serviceType": "Psychiatric expert witness reports",
              "founder": {
                "@type": "Person",
                "name": "Dr Imtiaz Syed",
                "jobTitle": "Independent Psychiatric Expert Witness"
              }
            }
          `}
        </script>
      </Helmet>
      <ErrorBoundary data-id="element-12" data-mp="1rjrc783k8a49">
        <SkipToContent data-id="element-13" data-mp="1rj7xs1d4c3uz" />
        <div className="flex flex-col min-h-screen" data-id="element-14" data-mp="1rl2et4l9rbts">
          <Header data-id="element-15" data-mp="1wiksf8a8ef84" />
          <ScrollToTop data-id="element-16" data-mp="1wj3zftycfj7d" />
          <main id="main-content" className="flex-grow" data-id="element-17" data-mp="1whj6u9vlaac5">
            <Routes data-id="element-18" data-mp="1hu7rdwwae66r">
              <Route path="/" element={<Home data-id="element-20" data-mp="1htetzmj7wt1a" />} data-id="element-19" data-mp="1hupvffp6xop8" />
              <Route path="/about" element={<About data-id="element-22" data-mp="1msj36d656qrm" />} data-id="element-21" data-mp="1hs3rrr5f7tca" />
              <Route path="/services" element={<Services data-id="element-24" data-mp="1mqy9eprnjbut" />} data-id="element-23" data-mp="1mrzn7201ycg8" />
              <Route path="/services/mental-capacity" element={<MentalCapacity data-id="element-26" data-mp="18vmdif1rffcb" />} data-id="element-25" data-mp="18tjfb7qgefwi" />
              <Route path="/services/trauma-ptsd" element={<TraumaPTSD data-id="element-28" data-mp="1dq029ylx2o80" />} data-id="element-27" data-mp="1dte3v4mratee" />
              <Route path="/services/specialist" element={<Specialist data-id="element-30" data-mp="ywn4uwgzvjpb" />} data-id="element-29" data-mp="yvlrglcial4f" />
              <Route path="/services/forensic-risk" element={<ForensicRisk data-id="element-32" data-mp="13uf46bjyy83j" />} data-id="element-31" data-mp="13u6t8p7epcuz" />
              <Route path="/services/neurodevelopmental" element={<Neurodevelopmental data-id="element-34" data-mp="2y7y05gycpimq" />} data-id="element-33" data-mp="2t8lakog3hxgg" />
              <Route path="/services/clinical-negligence" element={<ClinicalNegligence data-id="element-36" data-mp="mlmcpevkqjko" />} data-id="element-35" data-mp="2y7o9rs9e99gi" />
              <Route path="/sitemap" element={<Sitemap data-id="element-38" data-mp="hmj9lzz5mnfc" />} data-id="element-37" data-mp="hlrpblb0cc4d" />
              <Route path="/faq" element={<FAQ data-id="element-40" data-mp="hjx0hvwm7zv0" />} data-id="element-39" data-mp="hnceswigt4vd" />
              <Route path="/contact" element={<Contact data-id="element-42" data-mp="wbfnidnw6unv" />} data-id="element-41" data-mp="wb5xiq2ukni6" />
              <Route path="/policies/privacy" element={<Privacy data-id="element-44" data-mp="rjfhc3h8b5iu" />} data-id="element-43" data-mp="wcqt8j0wechm" />
              <Route path="/policies/terms" element={<Terms data-id="element-46" data-mp="ri4cs1fyltqo" />} data-id="element-45" data-mp="rj5swkbu16f9" />
              <Route path="/policies/cancellation" element={<Cancellation data-id="element-48" data-mp="159reigso7ugu" />} data-id="element-47" data-mp="15asyxbvkttb8" />
              <Route path="*" element={<NotFound data-id="element-50" data-mp="11aa8nm9c0vb5" />} data-id="element-49" data-mp="119qwyna9z35k" />
            </Routes>
          </main>
          <Footer data-id="element-51" data-mp="11c3beqgtqvdv" />
          <CookieConsent data-id="element-52" data-mp="1f99teitq4q21" />
        </div>
      </ErrorBoundary>
    </BrowserRouter>;
}